import React, { useState, useRef, useContext } from 'react';
import { motion } from 'framer-motion';
import { User, Phone, Mail, Edit3, Save, X, Camera, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import ConfirmModal from '@/components/shared/ConfirmModal';
import axios from 'axios';
import { UserContext } from '@/lib/User';
import toast from 'react-hot-toast';

const API = 'https://xceededge.com';

export default function AccountDetails({ user, course, onCourseUpdate }) {
  const { user: ctxUser } = useContext(UserContext);
  const session = ctxUser?.session || user?.session;

  const [editing, setEditing]       = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [saving, setSaving]         = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);

  const [form, setForm] = useState({
    name:   course?.full_name   || user?.full_name   || user?.name   || '',
    email:  course?.email       || user?.email       || '',
    mobile: course?.phone_number || user?.phone_number || user?.mobile || '',
  });

  const fileInputRef = useRef(null);
  const [localPhoto, setLocalPhoto] = useState(null); // instant preview before server confirms
  const photoUrl = localPhoto || course?.profile_photo || null;

  const initials = (course?.full_name || user?.full_name || 'S')
    .split(' ').map(w => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();

  const enrolledCount = course?.courses_enrolled ?? 0;

  // ── Photo upload ──────────────────────────────────────────────────────────
  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowed = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowed.includes(file.type)) {
      toast.error('Only JPG, PNG, or WebP images are allowed');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be under 5 MB');
      return;
    }

    if (!session) {
      toast.error('Session expired — please log in again.');
      return;
    }

    // Show instant local preview so user sees feedback immediately
    const objectUrl = URL.createObjectURL(file);
    setLocalPhoto(objectUrl);

    setPhotoUploading(true);
    try {
      const fd = new FormData();
      fd.append('photo', file);
      fd.append('session', session);

      // Do NOT set Content-Type manually — axios sets it automatically with
      // the correct multipart boundary, which PHP needs to parse the file.
      const res = await axios.post(`${API}/upload-profile-photo.php`, fd);

      if (res.data.success) {
        onCourseUpdate?.({ profile_photo: res.data.url });
        setLocalPhoto(null);            // switch to the permanent server URL
        URL.revokeObjectURL(objectUrl); // free browser memory
        // Cache the URL so it survives page refreshes even if the old backend
        // PHP doesn't return profile_photo yet
        // Same key used in CourseDashboard — email is stable across logout/login
        const cacheKey = `xceedEdge_photo_${ctxUser?.email || user?.email || 'u'}`;
        localStorage.setItem(cacheKey, res.data.url);
        toast.success('Profile photo updated');
      } else {
        toast.error(res.data.message || 'Upload failed');
      }
    } catch (err) {
      // Revert local preview on failure
      setLocalPhoto(null);
      URL.revokeObjectURL(objectUrl);
      const msg = err.response?.data?.message || err.message || 'Upload failed';
      toast.error(msg);
      console.error('Photo upload error:', err.response?.data ?? err);
    } finally {
      setPhotoUploading(false);
      e.target.value = '';
    }
  };

  // ── Save profile ──────────────────────────────────────────────────────────
  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.post(`${API}/update-profile.php`, {
        session,
        full_name:    form.name,
        email:        form.email,
        phone_number: form.mobile,
      });

      const ok = res.data.success || res.data.message === 'Profile updated successfully';
      if (ok) {
        onCourseUpdate?.({ full_name: form.name, email: form.email, phone_number: form.mobile });
        toast.success('Profile updated');
        setShowConfirm(false);
        setEditing(false);
      } else {
        toast.error(res.data.error || res.data.message || 'Update failed');
        setShowConfirm(false);
      }
    } catch {
      toast.error('Server error. Please try again.');
      setShowConfirm(false);
    } finally {
      setSaving(false);
    }
  };

  const fields = [
    { key: 'name',   label: 'Full Name',      Icon: User },
    { key: 'email',  label: 'Email Address',  Icon: Mail },
    { key: 'mobile', label: 'Mobile Number',  Icon: Phone },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold tracking-tight">Account Details</h2>
          <p className="text-muted-foreground text-sm mt-1">Manage your profile and account settings</p>
        </div>
        <Button
          variant={editing ? 'outline' : 'default'}
          size="sm"
          onClick={() => setEditing(!editing)}
          className="gap-2 rounded-xl font-semibold"
        >
          {editing
            ? <><X className="w-4 h-4" />Cancel</>
            : <><Edit3 className="w-4 h-4" />Edit Profile</>}
        </Button>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* ── Profile Info ── */}
        <div className="glass rounded-2xl border border-white/80 p-6 space-y-4">
          <h3 className="font-heading font-bold text-base border-b border-black/6 pb-3">Profile Information</h3>

          {fields.map(({ key, label, Icon }, i) => (
            <motion.div key={key}
              initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.06 }}
              className="flex items-center gap-3"
            >
              <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Icon className="w-4 h-4 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-muted-foreground font-medium">{label}</p>
                {editing ? (
                  <Input
                    type="text"
                    value={form[key]}
                    onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                    className="mt-1 h-8 text-sm rounded-lg border-black/15"
                  />
                ) : (
                  <p className="text-sm font-semibold text-foreground mt-0.5 truncate">{form[key]}</p>
                )}
              </div>
            </motion.div>
          ))}

          {editing && (
            <Button
              onClick={() => setShowConfirm(true)}
              className="w-full gap-2 rounded-xl bg-primary text-primary-foreground font-bold shadow-lg shadow-primary/25"
            >
              <Save className="w-4 h-4" /> Save Changes
            </Button>
          )}
        </div>

        {/* ── Right column ── */}
        <div className="space-y-4">
          {/* Activity */}
          <div className="glass rounded-2xl border border-white/80 p-6">
            <h3 className="font-heading font-bold text-base border-b border-black/6 pb-3 mb-4">Account Activity</h3>
            <div className="space-y-3">
              {[
                { label: 'Courses Enrolled', value: `${enrolledCount} ${enrolledCount === 1 ? 'Course' : 'Courses'}` },
                { label: 'Account Status',   value: 'Active (PRO)', highlight: true },
              ].map(({ label, value, highlight }, i) => (
                <motion.div key={label}
                  initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.07 }}
                  className="flex justify-between items-center p-3 rounded-xl bg-white/50 border border-white/80"
                >
                  <span className="text-sm text-muted-foreground">{label}</span>
                  <span className={`text-sm font-bold ${highlight ? 'text-primary' : 'text-foreground'}`}>{value}</span>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Profile card with photo upload */}
          <div className="glass rounded-2xl border border-white/80 p-5 flex items-center gap-4">
            {/* Avatar / photo upload trigger */}
            <div className="relative flex-shrink-0">
              <div className="w-16 h-16 rounded-2xl overflow-hidden bg-primary/15 flex items-center justify-center">
                {photoUrl ? (
                  <img src={photoUrl} alt="Profile" className="w-full h-full object-cover" />
                ) : (
                  <span className="font-heading font-black text-2xl text-primary">{initials}</span>
                )}
              </div>

              {/* Camera overlay button */}
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={photoUploading}
                className="absolute -bottom-1.5 -right-1.5 w-6 h-6 rounded-full bg-primary text-ink flex items-center justify-center shadow-md hover:bg-primary/90 transition disabled:opacity-60"
                title="Change profile photo"
              >
                {photoUploading
                  ? <Loader2 className="w-3 h-3 animate-spin" />
                  : <Camera className="w-3 h-3" />}
              </button>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={handlePhotoChange}
              />
            </div>

            <div className="min-w-0">
              <p className="font-heading font-bold text-base truncate">{form.name || 'Student'}</p>
              <p className="text-xs text-muted-foreground truncate">{form.email || '—'}</p>
              <span className="inline-flex items-center gap-1 mt-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-primary/15 text-primary">
                PRO Member
              </span>
              <p className="text-[10px] text-muted-foreground mt-1">
                Tap <Camera className="w-3 h-3 inline" /> to update photo
              </p>
            </div>
          </div>
        </div>
      </div>

      <ConfirmModal
        open={showConfirm}
        title="Save Profile Changes?"
        description="Your profile information will be updated with the new details you entered."
        confirmLabel="Save Changes"
        loading={saving}
        onConfirm={handleSave}
        onCancel={() => setShowConfirm(false)}
      />
    </div>
  );
}
